import { Grid, Paper } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import defaultLogo from '@percent/admin-dashboard/common/assets/images/default-logo.png'
import { ErrorView, Loader, TabPanel } from '@percent/admin-dashboard/common/components'
import { OrganisationRelationships } from '@percent/admin-dashboard/common/components/organisationRelationship/OrganisationRelationships'
import {
  useActivityAccess,
  useActivityTagsState,
  useQuery,
  useQueryList,
  useRequiredParams,
  useVettingEventsAccess
} from '@percent/admin-dashboard/common/hooks'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { Avatar, Heading, Spacer, Tabs } from '@percent/lemonade'
import { ActivityLogTable } from './activityLog/activityLogTable/ActivityLogTable'
import { ComplianceDetails } from './components/complianceDetails/ComplianceDetails'
import { History } from './components/history/History'
import { AccountStatusAndTags } from './components/overviewDetails/accountStatusAndTags/AccountStatusAndTags'
import { Brand } from './components/overviewDetails/brand/Brand'
import { Contacts } from './components/overviewDetails/contacts/Contacts'
import { Donations } from './components/overviewDetails/donations/Donations'
import { Location } from './components/overviewDetails/location/Location'
import { OfficialDetails } from './components/overviewDetails/officialDetails/OfficialDetails'
import { PaymentDetails } from './components/overviewDetails/paymentDetails/PaymentDetails'
import { UnclaimOrganization } from './components/overviewDetails/unclaimOrganization/UnclaimOrganization'
import { MilestonesContainer } from './milestones/MilestonesContainer'
import { Members } from './members/Members'
import styles from './OrganizationsDetail.module.scss'
import { Files } from './components/overviewDetails/files/Files'
import { OrganisationTypes } from '@percent/domain/verifications'
import { ChecksSummary } from './components/overviewDetails/checksSummary/ChecksSummary'

export function OrganizationsDetail() {
  const location = useLocation()
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const { id } = useRequiredParams()
  const { adminService, eligibilityService } = useServices()
  const paymentMethodsReadFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_PAYMENT_METHODS_READ)
  const navigate = useNavigate()
  const { activityTags: tagsData, isLoading: isTagsLoading, errorMessage: tagsErrorMessage } = useActivityTagsState()

  const [{ data, isLoading, errorMessage }, { refresh }] = useQuery(adminService.getOrganisation, { id })

  const [{ data: paymentMethodData, isLoading: isPaymentMethodLoading, status }, { refresh: paymentMethodRefresh }] =
    useQuery(adminService.getOrganisationPaymentMethod, { id })

  const { canRead: canReadVettingEvents } = useVettingEventsAccess()

  // Skipped rather than merely hidden: this runs at page level, outside the compliance
  // tab, so without the skip it would 403 for a caller who cannot read vetting events.
  const [
    { data: vettingEventsResults, errorMessage: errorVettingEventsResults },
    { refresh: refreshVettingEventsResults }
  ] = useQuery(adminService.getVettingEventsResults, { organisationId: id }, { skip: !canReadVettingEvents })

  const { canRead: canReadActivity } = useActivityAccess()

  const [
    { isLoading: isActivityEventsLoading, errorMessageOrNull, dataOrNull, totalResults },
    { nextPage, previousPage, query: queryActivityEvents }
  ] = useQueryList(eligibilityService.getActivityEvents, { organisationId: id }, false)

  useEffect(() => {
    if (canReadActivity) {
      queryActivityEvents({ organisationId: id })
    }
  }, [canReadActivity, id, queryActivityEvents])

  useEffect(() => {
    if (location) {
      setValue(`${location.pathname}`)
    }
  }, [location])

  const handleChange = (newValue: string) => {
    setValue(newValue)
    navigate(newValue)
  }

  // Deliberately not gated on the vetting events query: `skip` only flips once the flags
  // resolve, so including it would swap the whole page back to a loader after it had
  // already rendered. The compliance tab waits on its own data instead.
  if (isLoading || isTagsLoading || isActivityEventsLoading || isPaymentMethodLoading) {
    return <Loader />
  }

  if (errorMessage || errorVettingEventsResults || tagsErrorMessage || errorMessageOrNull) {
    return (
      <ErrorView errorMessage={errorMessageOrNull || errorMessage || errorVettingEventsResults || tagsErrorMessage} />
    )
  }

  return (
    <>
      <Grid container spacing={0} component={Paper} elevation={0} className={styles.container}>
        <Grid item xs={12} className={styles.nonprofitDetail}>
          <Avatar image={data.logo ?? defaultLogo} alt="organization-logo" size="medium" />
          <Spacer size={4} axis="horizontal" />
          <Heading level="h6">{data.name}</Heading>
        </Grid>
        <Tabs
          activeTab={value}
          onChangeTab={handleChange}
          items={[
            { key: `/organizations/${data.id}`, label: t('typography.overview') },
            {
              key: `/organizations/${data.id}/milestones`,
              label: t('typography.milestones')
            },
            ...(canReadVettingEvents
              ? [{ key: `/organizations/${data.id}/compliance`, label: t('typography.compliance') }]
              : []),
            { key: `/organizations/${data.id}/activity-log`, label: t('typography.activityLog') },
            { key: `/organizations/${data.id}/members`, label: t('typography.members') }
          ]}
        />
      </Grid>
      <TabPanel value={value} index={`/organizations/${data.id}`}>
        <OfficialDetails organization={data} refresh={refresh} />
        <ChecksSummary organizationId={id} />
        {data.relationships && data.relationships.length > 0 && <Spacer size={6} axis="vertical" />}
        <OrganisationRelationships relationships={data.relationships} />
        <Spacer size={6} axis="vertical" />
        <AccountStatusAndTags
          tagsData={tagsData}
          event={dataOrNull?.[0]}
          organisationTypes={
            Object.keys(data?.types ?? {})?.filter(type => data?.types?.[type as OrganisationTypes] === 'yes') || []
          }
          organisationId={id}
        />
        <Donations organisationId={id} organisationName={data.name} />
        {paymentMethodsReadFeatureFlag && (
          <PaymentDetails
            organization={data}
            paymentMethod={status === 200 ? paymentMethodData : undefined}
            paymentMethodRefresh={paymentMethodRefresh}
          />
        )}
        <Brand organization={data} refresh={refresh} />
        <Files organization={data} refresh={refresh} />
        <Contacts organization={data} refresh={refresh} />
        <Location organization={data} refresh={refresh} />
        <UnclaimOrganization organization={data} refresh={refresh} />
      </TabPanel>
      <TabPanel value={value} index={`/organizations/${data.id}/milestones`}>
        <MilestonesContainer />
      </TabPanel>
      {canReadVettingEvents && vettingEventsResults && (
        <TabPanel value={value} index={`/organizations/${data.id}/compliance`}>
          <div className={styles.complianceContainer}>
            <ComplianceDetails vettingEventsResults={vettingEventsResults} organisation={data} />
            <History
              nonprofitName={data.name}
              organisationId={id}
              refreshVettingEventsResults={refreshVettingEventsResults}
              vettingEventsResults={vettingEventsResults}
            />
          </div>
        </TabPanel>
      )}
      <TabPanel value={value} index={`/organizations/${data.id}/activity-log`}>
        <ActivityLogTable
          data={dataOrNull}
          errorMessage={errorMessageOrNull}
          isLoading={isActivityEventsLoading}
          totalResults={totalResults}
          tagsData={tagsData}
          nextPage={nextPage}
          previousPage={previousPage}
        />
      </TabPanel>
      <TabPanel value={value} index={`/organizations/${data.id}/members`}>
        <Members />
      </TabPanel>
    </>
  )
}
